import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ApiError,
  api,
  type Run,
  type RunSchedule,
  type RunScheduleFrequency,
  type RunScheduleMonthPolicy,
  type RunSchedulesPage,
  type RunScheduleStatus,
  type RunStatus,
  type RunsPage,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InfoHint } from '../components/InfoHint';
import { RunAdminActions } from '../components/RunAdminActions';
import { STAGE_LABELS, getRunProgress } from '../hooks/useRunPolling';
import { usePersistedTableState } from '../hooks/usePersistedTableState';
import {
  clampMonthInputValue,
  currentMonthInputValue,
  openNativeDatePicker,
} from '../lib/runSchedule';

const STATUS_PILL: Record<RunStatus, string> = {
  CREATED: 'pill-info',
  RUNNING: 'pill-warn',
  SUCCESS: 'pill-success',
  FAILED: 'pill-danger',
};

const SCHEDULE_STATUS_PILL: Record<RunScheduleStatus, string> = {
  ACTIVE: 'pill-success',
  PAUSED: 'pill-warn',
  CANCELLED: 'pill-danger',
  DELETED: 'pill-neutral',
};

const SCHEDULE_STATUS_LABELS: Record<RunScheduleStatus, string> = {
  ACTIVE: 'Активно',
  PAUSED: 'Пауза',
  CANCELLED: 'Отменено',
  DELETED: 'Удалено',
};

const FREQUENCY_LABELS: Record<RunScheduleFrequency, string> = {
  MONTHLY: 'Ежемесячно',
  WEEKLY: 'Еженедельно',
  BIWEEKLY: 'Раз в две недели',
};

const MONTH_POLICY_LABELS: Record<RunScheduleMonthPolicy, string> = {
  PREVIOUS_COMPLETED: 'Предыдущий завершённый',
  CURRENT: 'Текущий',
  FIXED: 'Фиксированный',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function formatMonth(month: string): string {
  const d = new Date(month);
  return d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

function runSummary(params: Record<string, unknown> | null): string {
  if (!params) return '—';
  const cats = (params.categories_scope as string[] | undefined)?.length ?? 0;
  const ai = params.llm_enabled ? 'AI' : 'без AI';
  const detail = Number(params.bhapi_top_n ?? 0);
  return `${cats} кат., ${ai}, дет. ${detail}`;
}

function scheduleTime(schedule: RunSchedule): string {
  const hh = String(schedule.hour).padStart(2, '0');
  const mm = String(schedule.minute).padStart(2, '0');
  return `${hh}:${mm} ${schedule.timezone}`;
}

function scheduleCategoriesCount(schedule: RunSchedule): string {
  const count = schedule.categories_scope.length;
  return `${count} кат.`;
}

type RunsTableState = {
  statusFilter: string;
  marketplaceFilter: string;
  monthFrom: string;
  monthTo: string;
  limit: number;
  offset: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
};

type RunSchedulesTableState = {
  scheduleStatusFilter: string;
  scheduleMarketplaceFilter: string;
  scheduleFrequencyFilter: string;
  scheduleMonthPolicyFilter: string;
  scheduleLimit: number;
  scheduleOffset: number;
  scheduleSortBy: string;
  scheduleSortDir: 'asc' | 'desc';
};

type RunsListTab = 'runs' | 'schedules';

const RUNS_TABLE_DEFAULTS: RunsTableState = {
  statusFilter: '',
  marketplaceFilter: '',
  monthFrom: '',
  monthTo: '',
  limit: 20,
  offset: 0,
  sortBy: 'created_at',
  sortDir: 'desc',
};
const RUNS_TABLE_SORTING_KEYS: readonly (keyof RunsTableState)[] = [
  'limit',
  'offset',
  'sortBy',
  'sortDir',
];
const RUNS_TABLE_FILTER_KEYS: readonly (keyof RunsTableState)[] = [
  'statusFilter',
  'marketplaceFilter',
  'monthFrom',
  'monthTo',
];

const RUN_SCHEDULES_TABLE_DEFAULTS: RunSchedulesTableState = {
  scheduleStatusFilter: '',
  scheduleMarketplaceFilter: '',
  scheduleFrequencyFilter: '',
  scheduleMonthPolicyFilter: '',
  scheduleLimit: 20,
  scheduleOffset: 0,
  scheduleSortBy: 'next_run_at',
  scheduleSortDir: 'asc',
};
const RUN_SCHEDULES_TABLE_SORTING_KEYS: readonly (keyof RunSchedulesTableState)[] = [
  'scheduleLimit',
  'scheduleOffset',
  'scheduleSortBy',
  'scheduleSortDir',
];
const RUN_SCHEDULES_TABLE_FILTER_KEYS: readonly (keyof RunSchedulesTableState)[] = [
  'scheduleStatusFilter',
  'scheduleMarketplaceFilter',
  'scheduleFrequencyFilter',
  'scheduleMonthPolicyFilter',
];

export function RunsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreateRun = user?.role === 'admin' || user?.role === 'user';
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [schedules, setSchedules] = useState<RunSchedule[] | null>(null);
  const [total, setTotal] = useState(0);
  const [scheduleTotal, setScheduleTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleActionId, setScheduleActionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<RunsListTab>('runs');
  const [tableState, setTableState] = usePersistedTableState(
    user?.id,
    'runs.list',
    RUNS_TABLE_DEFAULTS,
    {
      sortingKeys: RUNS_TABLE_SORTING_KEYS,
      filterKeys: RUNS_TABLE_FILTER_KEYS,
      persistSorting: Boolean(user?.persist_table_sorting),
      persistFilters: Boolean(user?.persist_table_filters),
    },
  );
  const [scheduleTableState, setScheduleTableState] = usePersistedTableState(
    user?.id,
    'runs.schedules',
    RUN_SCHEDULES_TABLE_DEFAULTS,
    {
      sortingKeys: RUN_SCHEDULES_TABLE_SORTING_KEYS,
      filterKeys: RUN_SCHEDULES_TABLE_FILTER_KEYS,
      persistSorting: Boolean(user?.persist_table_sorting),
      persistFilters: Boolean(user?.persist_table_filters),
    },
  );
  const {
    statusFilter,
    marketplaceFilter,
    monthFrom,
    monthTo,
    limit,
    offset,
    sortBy,
    sortDir,
  } = tableState;
  const {
    scheduleStatusFilter,
    scheduleMarketplaceFilter,
    scheduleFrequencyFilter,
    scheduleMonthPolicyFilter,
    scheduleLimit,
    scheduleOffset,
    scheduleSortBy,
    scheduleSortDir,
  } = scheduleTableState;
  const maxMonth = currentMonthInputValue();

  function updateTableState(patch: Partial<RunsTableState>) {
    setTableState((prev) => ({ ...prev, ...patch }));
  }

  function updateScheduleTableState(patch: Partial<RunSchedulesTableState>) {
    setScheduleTableState((prev) => ({ ...prev, ...patch }));
  }

  useEffect(() => {
    const clampedMonthFrom = clampMonthInputValue(monthFrom);
    const clampedMonthTo = clampMonthInputValue(monthTo);
    if (clampedMonthFrom !== monthFrom || clampedMonthTo !== monthTo) {
      setTableState((prev) => ({
        ...prev,
        monthFrom: clampedMonthFrom,
        monthTo: clampedMonthTo,
        offset: 0,
      }));
    }
  }, [monthFrom, monthTo, setTableState]);

  const loadRuns = useCallback(() => {
    const q = new URLSearchParams();
    if (statusFilter) q.set('status', statusFilter);
    if (marketplaceFilter) q.set('marketplace', marketplaceFilter);
    if (monthFrom) q.set('month_from', monthFrom);
    if (monthTo) q.set('month_to', monthTo);
    q.set('limit', String(limit));
    q.set('offset', String(offset));
    q.set('sort_by', sortBy);
    q.set('sort_dir', sortDir);
    const qs = q.toString();
    api
      .get<RunsPage>(`/api/runs?${qs}`)
      .then((data) => {
        setError(null);
        setRuns(data.items);
        setTotal(data.total);
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : String(err)),
      );
  }, [
    statusFilter,
    marketplaceFilter,
    monthFrom,
    monthTo,
    limit,
    offset,
    sortBy,
    sortDir,
  ]);

  const loadSchedules = useCallback(async () => {
    const q = new URLSearchParams();
    if (scheduleStatusFilter) q.set('status', scheduleStatusFilter);
    if (scheduleMarketplaceFilter) q.set('marketplace', scheduleMarketplaceFilter);
    if (scheduleFrequencyFilter) q.set('frequency', scheduleFrequencyFilter);
    if (scheduleMonthPolicyFilter) q.set('month_policy', scheduleMonthPolicyFilter);
    q.set('limit', String(scheduleLimit));
    q.set('offset', String(scheduleOffset));
    q.set('sort_by', scheduleSortBy);
    q.set('sort_dir', scheduleSortDir);
    const scheduleQs = q.toString();
    try {
      const data = await api.get<RunSchedulesPage>(`/api/run-schedules?${scheduleQs}`);
      setScheduleError(null);
      setSchedules(data.items);
      setScheduleTotal(data.total);
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : String(err));
    }
  }, [
    scheduleStatusFilter,
    scheduleMarketplaceFilter,
    scheduleFrequencyFilter,
    scheduleMonthPolicyFilter,
    scheduleLimit,
    scheduleOffset,
    scheduleSortBy,
    scheduleSortDir,
  ]);

  useEffect(() => {
    loadRuns();
    void loadSchedules();
    const id = window.setInterval(() => {
      loadRuns();
      void loadSchedules();
    }, 4000);
    return () => window.clearInterval(id);
  }, [loadRuns, loadSchedules]);

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const scheduleCurrentPage = Math.floor(scheduleOffset / scheduleLimit) + 1;
  const scheduleTotalPages = Math.max(1, Math.ceil(scheduleTotal / scheduleLimit));

  function updateSort(key: string) {
    if (sortBy === key) {
      updateTableState({
        offset: 0,
        sortDir: sortDir === 'asc' ? 'desc' : 'asc',
      });
    } else {
      updateTableState({ offset: 0, sortBy: key, sortDir: 'asc' });
    }
  }

  function SortHeader({ label, value }: { label: string; value: string }) {
    const active = sortBy === value;
    return (
      <button
        type="button"
        className="text-caption text-text-secondary hover:text-text-primary"
        onClick={() => updateSort(value)}
      >
        {label}
        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    );
  }

  function updateScheduleSort(key: string) {
    if (scheduleSortBy === key) {
      updateScheduleTableState({
        scheduleOffset: 0,
        scheduleSortDir: scheduleSortDir === 'asc' ? 'desc' : 'asc',
      });
    } else {
      updateScheduleTableState({
        scheduleOffset: 0,
        scheduleSortBy: key,
        scheduleSortDir: 'asc',
      });
    }
  }

  function ScheduleSortHeader({ label, value }: { label: string; value: string }) {
    const active = scheduleSortBy === value;
    return (
      <button
        type="button"
        className="text-caption text-text-secondary hover:text-text-primary"
        onClick={() => updateScheduleSort(value)}
      >
        {label}
        {active ? (scheduleSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    );
  }

  async function changeScheduleStatus(
    schedule: RunSchedule,
    action: 'pause' | 'resume' | 'cancel',
  ) {
    if (action === 'cancel' && !window.confirm('Отменить расписание?')) return;
    setScheduleActionId(schedule.id);
    setScheduleError(null);
    try {
      await api.post<RunSchedule>(
        `/api/run-schedules/${schedule.id}/${action}`,
      );
      await loadSchedules();
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setScheduleActionId(null);
    }
  }

  async function runScheduleNow(schedule: RunSchedule) {
    setScheduleActionId(schedule.id);
    setScheduleError(null);
    try {
      const run = await api.post<Run>(
        `/api/run-schedules/${schedule.id}/run-now`,
      );
      await loadSchedules();
      navigate(`/runs/${run.id}`);
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setScheduleActionId(null);
    }
  }

  async function deleteSchedule(schedule: RunSchedule) {
    const ok = window.confirm(
      'Удалить расписание? Если по нему уже были прогоны, запись будет скрыта, а история останется доступной администратору для восстановления.',
    );
    if (!ok) return;
    setScheduleActionId(schedule.id);
    setScheduleError(null);
    try {
      await api.delete(`/api/run-schedules/${schedule.id}`);
      await loadSchedules();
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setScheduleActionId(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="section-header page-header">
        <div>
          <div className="rubric">История</div>
          <h1 className="inline-flex items-center gap-2 text-h1 font-strong text-text-primary">
            Прогоны
            <InfoHint text="Здесь отображаются ручные запуски и прогоны, созданные по расписанию. Фильтры и сортировку можно сохранять в профиле." />
          </h1>
        </div>
        {canCreateRun ? (
          <Link to="/runs/new" className="btn-primary btn-md">
            Новый прогон
          </Link>
        ) : null}
      </div>

      <div
        role="tablist"
        aria-label="Разделы прогонов"
        className="flex flex-wrap gap-2"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'runs'}
          className={
            activeTab === 'runs' ? 'btn-subtle btn-sm' : 'btn-ghost btn-sm'
          }
          onClick={() => setActiveTab('runs')}
        >
          История прогонов
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'schedules'}
          className={
            activeTab === 'schedules' ? 'btn-subtle btn-sm' : 'btn-ghost btn-sm'
          }
          onClick={() => setActiveTab('schedules')}
        >
          Расписания
        </button>
      </div>

      {activeTab === 'schedules' ? (
        <div className="card card-padding flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="rubric">План</div>
              <h2 className="inline-flex items-center gap-2 text-h3 font-strong text-text-primary">
                Сохранённые расписания
                <InfoHint text="Расписание хранит будущий сценарий запуска. Фактический прогон появится в истории после срабатывания scheduler." />
              </h2>
            </div>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => void loadSchedules()}
            >
              Обновить
            </button>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <label className="block">
              <span className="form-label">Статус</span>
              <select
                className="input mt-1"
                value={scheduleStatusFilter}
                onChange={(e) =>
                  updateScheduleTableState({
                    scheduleOffset: 0,
                    scheduleStatusFilter: e.target.value,
                  })
                }
              >
                <option value="">Все</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="PAUSED">PAUSED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </label>
            <label className="block">
              <span className="form-label">Маркетплейс</span>
              <select
                className="input mt-1"
                value={scheduleMarketplaceFilter}
                onChange={(e) =>
                  updateScheduleTableState({
                    scheduleOffset: 0,
                    scheduleMarketplaceFilter: e.target.value,
                  })
                }
              >
                <option value="">Все</option>
                <option value="WB">WB</option>
              </select>
            </label>
            <label className="block">
              <span className="form-label">Частота</span>
              <select
                className="input mt-1"
                value={scheduleFrequencyFilter}
                onChange={(e) =>
                  updateScheduleTableState({
                    scheduleOffset: 0,
                    scheduleFrequencyFilter: e.target.value,
                  })
                }
              >
                <option value="">Все</option>
                <option value="MONTHLY">Ежемесячно</option>
                <option value="WEEKLY">Еженедельно</option>
                <option value="BIWEEKLY">Раз в две недели</option>
              </select>
            </label>
            <label className="block">
              <span className="form-label">Месяц отчёта</span>
              <select
                className="input mt-1"
                value={scheduleMonthPolicyFilter}
                onChange={(e) =>
                  updateScheduleTableState({
                    scheduleOffset: 0,
                    scheduleMonthPolicyFilter: e.target.value,
                  })
                }
              >
                <option value="">Все</option>
                <option value="PREVIOUS_COMPLETED">Предыдущий завершённый</option>
                <option value="CURRENT">Текущий</option>
                <option value="FIXED">Фиксированный</option>
              </select>
            </label>
            <label className="block">
              <span className="form-label">На странице</span>
              <select
                className="input mt-1"
                value={scheduleLimit}
                onChange={(e) =>
                  updateScheduleTableState({
                    scheduleOffset: 0,
                    scheduleLimit: Number(e.target.value),
                  })
                }
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {scheduleError ? (
            <div className="error-hint">{scheduleError}</div>
          ) : null}
          {schedules === null && !scheduleError ? (
            <div className="text-caption text-text-tertiary">
              Загрузка расписаний…
            </div>
          ) : null}
          {schedules && scheduleTotal === 0 ? (
            <div className="text-caption text-text-tertiary">
              Расписаний под выбранные фильтры нет.{' '}
              {canCreateRun ? (
                <Link to="/runs/new" className="text-accent hover:underline">
                  Создать расписание
                </Link>
              ) : (
                'Пользователи с правом запуска ещё не сохраняли сценарии.'
              )}
            </div>
          ) : null}
          {schedules && schedules.length > 0 ? (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>
                        <ScheduleSortHeader label="Название" value="title" />
                      </th>
                      <th>
                        <ScheduleSortHeader label="МП" value="marketplace" />
                      </th>
                      <th>
                        <ScheduleSortHeader label="Частота" value="frequency" />
                      </th>
                      <th>
                        <ScheduleSortHeader label="Месяц отчёта" value="month_policy" />
                      </th>
                      <th>Время</th>
                      <th>Категории</th>
                      <th>
                        <ScheduleSortHeader label="Статус" value="status" />
                      </th>
                      <th>
                        <ScheduleSortHeader label="Следующий запуск" value="next_run_at" />
                      </th>
                      <th>
                        <ScheduleSortHeader label="Последний запуск" value="last_run_at" />
                      </th>
                      {canCreateRun ? <th></th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((schedule) => (
                      <tr key={schedule.id}>
                        <td className="text-text-primary max-w-[220px]">
                          <div>{schedule.title}</div>
                          <div className="mono text-caption text-text-tertiary">
                            {schedule.id.slice(0, 8)}
                          </div>
                        </td>
                        <td className="mono">{schedule.marketplace}</td>
                        <td>{FREQUENCY_LABELS[schedule.frequency]}</td>
                        <td>{MONTH_POLICY_LABELS[schedule.month_policy]}</td>
                        <td className="mono text-caption">
                          {scheduleTime(schedule)}
                        </td>
                        <td className="text-caption text-text-secondary">
                          {scheduleCategoriesCount(schedule)}
                        </td>
                        <td>
                          <span className={SCHEDULE_STATUS_PILL[schedule.status]}>
                            {SCHEDULE_STATUS_LABELS[schedule.status]}
                          </span>
                        </td>
                        <td className="text-caption">
                          {schedule.status === 'CANCELLED'
                            ? '—'
                            : formatDate(schedule.next_run_at)}
                        </td>
                        <td className="text-caption">
                          {formatDate(schedule.last_run_at)}
                        </td>
                        {canCreateRun ? (
                          <td>
                            <div className="flex flex-wrap gap-2">
                              <Link
                                to={`/runs/new?scheduleId=${schedule.id}`}
                                className="btn-ghost btn-sm"
                              >
                                Настроить
                              </Link>
                              {schedule.status !== 'CANCELLED' ? (
                                <button
                                  type="button"
                                  className="btn-ghost btn-sm"
                                  disabled={scheduleActionId === schedule.id}
                                  onClick={() => void runScheduleNow(schedule)}
                                >
                                  Запустить
                                </button>
                              ) : null}
                              {schedule.status === 'ACTIVE' ? (
                                <button
                                  type="button"
                                  className="btn-ghost btn-sm"
                                  disabled={scheduleActionId === schedule.id}
                                  onClick={() =>
                                    void changeScheduleStatus(schedule, 'pause')
                                  }
                                >
                                  Пауза
                                </button>
                              ) : null}
                              {schedule.status === 'PAUSED' ? (
                                <button
                                  type="button"
                                  className="btn-ghost btn-sm"
                                  disabled={scheduleActionId === schedule.id}
                                  onClick={() =>
                                    void changeScheduleStatus(schedule, 'resume')
                                  }
                                >
                                  Возобновить
                                </button>
                              ) : null}
                              {schedule.status !== 'CANCELLED' ? (
                                <button
                                  type="button"
                                  className="btn-ghost btn-sm text-danger"
                                  disabled={scheduleActionId === schedule.id}
                                  onClick={() =>
                                    void changeScheduleStatus(schedule, 'cancel')
                                  }
                                >
                                  Отменить
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="btn-ghost btn-sm text-danger"
                                disabled={scheduleActionId === schedule.id}
                                onClick={() => void deleteSchedule(schedule)}
                              >
                                Удалить
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-3 text-caption text-text-secondary">
                <span>
                  {scheduleOffset + 1}–{Math.min(scheduleOffset + scheduleLimit, scheduleTotal)} из {scheduleTotal}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-subtle btn-sm"
                    disabled={scheduleOffset === 0}
                    onClick={() =>
                      updateScheduleTableState({
                        scheduleOffset: Math.max(0, scheduleOffset - scheduleLimit),
                      })
                    }
                  >
                    Назад
                  </button>
                  <span>
                    {scheduleCurrentPage} / {scheduleTotalPages}
                  </span>
                  <button
                    type="button"
                    className="btn-subtle btn-sm"
                    disabled={scheduleOffset + scheduleLimit >= scheduleTotal}
                    onClick={() =>
                      updateScheduleTableState({
                        scheduleOffset: scheduleOffset + scheduleLimit,
                      })
                    }
                  >
                    Вперёд
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {activeTab === 'runs' ? (
        <>
          <div className="flex flex-wrap gap-3 items-end">
            <label className="block">
              <span className="form-label inline-flex items-center gap-1">
                Статус
                <InfoHint text="Статус показывает итог выполнения: создан, выполняется, завершён успешно или с ошибкой." />
              </span>
              <select
                className="input mt-1"
                value={statusFilter}
                onChange={(e) => {
                  updateTableState({ offset: 0, statusFilter: e.target.value });
                }}
              >
                <option value="">Все</option>
                <option value="RUNNING">RUNNING</option>
                <option value="CREATED">CREATED</option>
                <option value="SUCCESS">SUCCESS</option>
                <option value="FAILED">FAILED</option>
              </select>
            </label>
            <label className="block">
              <span className="form-label">Маркетплейс</span>
              <select
                className="input mt-1"
                value={marketplaceFilter}
                onChange={(e) => {
                  updateTableState({
                    offset: 0,
                    marketplaceFilter: e.target.value,
                  });
                }}
              >
                <option value="">Все</option>
                <option value="WB">WB</option>
              </select>
            </label>
            <label className="block">
              <span className="form-label inline-flex items-center gap-1">
                Месяц от
                <InfoHint text="Фильтр ищет прогоны по месяцу анализа, а не по дате запуска." />
              </span>
              <input
                className="input mt-1"
                type="month"
                max={maxMonth}
                value={monthFrom}
                onClick={(e) => openNativeDatePicker(e.currentTarget)}
                onChange={(e) =>
                  updateTableState({
                    offset: 0,
                    monthFrom: clampMonthInputValue(e.target.value),
                  })
                }
              />
            </label>
            <label className="block">
              <span className="form-label">Месяц до</span>
              <input
                className="input mt-1"
                type="month"
                max={maxMonth}
                value={monthTo}
                onClick={(e) => openNativeDatePicker(e.currentTarget)}
                onChange={(e) =>
                  updateTableState({
                    offset: 0,
                    monthTo: clampMonthInputValue(e.target.value),
                  })
                }
              />
            </label>
            <label className="block">
              <span className="form-label">На странице</span>
              <select
                className="input mt-1"
                value={limit}
                onChange={(e) => {
                  updateTableState({
                    offset: 0,
                    limit: Number(e.target.value),
                  });
                }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error ? <div className="error-hint">{error}</div> : null}

          {runs === null && !error ? (
            <div className="card card-padding text-text-tertiary">
              Загрузка…
            </div>
          ) : null}

          {runs && runs.length === 0 ? (
            <div className="card card-padding text-text-tertiary">
              Прогонов ещё нет.{' '}
              {canCreateRun ? (
                <Link to="/runs/new" className="text-accent hover:underline">
                  Создать первый
                </Link>
              ) : (
                'Администратор ещё не запускал анализ.'
              )}
            </div>
          ) : null}

          {runs && runs.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <SortHeader label="Название" value="title" />
                    </th>
                    <th>
                      <SortHeader label="Месяц" value="month" />
                    </th>
                    <th>
                      <SortHeader label="МП" value="marketplace" />
                    </th>
                    <th>Параметры</th>
                    <th>
                      <SortHeader label="Статус" value="status" />
                    </th>
                    <th>Этап</th>
                    <th>Новые</th>
                    <th>Гипер-рост</th>
                    <th>
                      <SortHeader label="Старт" value="started_at" />
                    </th>
                    <th>
                      <SortHeader label="Финиш" value="finished_at" />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => {
                    const stats = r.stats as {
                      analytics?: {
                        new_winners?: number;
                        hyper_growth?: number;
                      };
                    } | null;
                    const newWinners = stats?.analytics?.new_winners ?? '—';
                    const hyper = stats?.analytics?.hyper_growth ?? '—';
                    const prog = getRunProgress(r);
                    const stageText = prog.current_stage
                      ? (STAGE_LABELS[prog.current_stage] ?? prog.current_stage)
                      : '—';
                    return (
                      <tr key={r.id}>
                        <td className="text-text-primary max-w-[220px]">
                          <Link
                            to={`/runs/${r.id}`}
                            className="hover:underline"
                          >
                            {r.title}
                          </Link>
                          <div className="mono text-caption text-text-tertiary">
                            {r.id.slice(0, 8)}
                          </div>
                        </td>
                        <td className="text-text-primary">
                          {formatMonth(r.month)}
                        </td>
                        <td className="mono">{r.marketplace}</td>
                        <td className="text-caption text-text-secondary max-w-[140px] truncate">
                          {runSummary(r.params)}
                        </td>
                        <td>
                          <span
                            className={STATUS_PILL[r.status]}
                            title={prog.current_detail ?? ''}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="text-caption text-text-secondary max-w-[160px] truncate">
                          {stageText}
                        </td>
                        <td className="mono text-text-primary">{newWinners}</td>
                        <td className="mono text-text-primary">{hyper}</td>
                        <td className="text-caption">
                          {formatDate(r.started_at)}
                        </td>
                        <td className="text-caption">
                          {formatDate(r.finished_at)}
                        </td>
                        <td className="space-y-1">
                          <Link
                            to={`/runs/${r.id}`}
                            className="btn-ghost btn-sm block"
                          >
                            Детали →
                          </Link>
                          {r.actions?.can_retry ||
                          r.actions?.can_delete ||
                          r.actions?.can_edit ||
                          r.actions?.can_cancel ? (
                            <RunAdminActions run={r} onUpdated={loadRuns} />
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {runs && total > 0 ? (
            <div className="flex items-center justify-between gap-3 text-caption text-text-secondary">
              <span>
                {offset + 1}–{Math.min(offset + limit, total)} из {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-subtle btn-sm"
                  disabled={offset === 0}
                  onClick={() =>
                    updateTableState({ offset: Math.max(0, offset - limit) })
                  }
                >
                  Назад
                </button>
                <span>
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-subtle btn-sm"
                  disabled={offset + limit >= total}
                  onClick={() => updateTableState({ offset: offset + limit })}
                >
                  Вперёд
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
